import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  ListItemIcon,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { getSales, Sale } from '../firebase/financeServices';

// Payment method options
const paymentMethods = [
  'All Methods',
  'cash',
  'card',
  'qr',
  'bank',
  'other'
];

// Payment method display names
const paymentMethodDisplayNames: Record<string, string> = {
  cash: 'Cash',
  card: 'Credit/Debit Card',
  qr: 'QR Payment',
  bank: 'Bank Transfer',
  other: 'Other'
};

// Status options
const statuses = [
  'All Statuses',
  'paid',
  'pending',
  'partially_paid'
];

// Status display names
const statusDisplayNames: Record<string, string> = {
  paid: 'Paid',
  pending: 'Pending',
  partially_paid: 'Partially Paid'
};

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return 'RM ' + new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper function to get item categories from a sale
const getSaleCategories = (sale: Sale): string[] => {
  const categories = new Set<string>();
  
  sale.items.forEach(item => {
    categories.add(item.type === 'product' ? 'Products' : 'Services');
  });
  
  return Array.from(categories);
};

// Helper function to format date
const formatDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    try {
      return format(parseISO(date), 'MMM dd, yyyy');
    } catch (e) {
      return date;
    }
  }
  return format(date, 'MMM dd, yyyy');
};

const Income: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // State for table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for date range
  const [startDate, setStartDate] = useState<Date | null>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  
  // State for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState('All Methods');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  
  // State for filter menu
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  
  // State for loading and sales data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);

  // Fetch sales data
  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const salesData = await getSales(startDate, endDate);
      setSales(salesData);
    } catch (err) {
      console.error('Error fetching sales:', err);
      setError('Failed to load income data. Please try again.');
      enqueueSnackbar('Failed to load income data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, enqueueSnackbar]);

  // Load sales data on component mount and when date range changes
  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Filtered sales based on search and filters
  const filteredSales = sales.filter((sale) => {
    // Skip voided sales
    if (sale.status === 'voided') return false;
    
    // Apply search filter
    const matchesSearch = searchQuery.trim() === '' || 
      sale.saleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sale.customerDetails?.name && sale.customerDetails.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      sale.staffName.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply payment method filter
    const matchesMethod = methodFilter === 'All Methods' || sale.paymentMethod === methodFilter;
    
    // Apply status filter
    const matchesStatus = statusFilter === 'All Statuses' || sale.paymentStatus === statusFilter;
    
    return matchesSearch && matchesMethod && matchesStatus;
  });

  // Handle pagination changes
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle filter changes
  const handleMethodChange = (event: SelectChangeEvent) => {
    setMethodFilter(event.target.value);
    setPage(0);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  // Handle date range changes
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Handle filter menu
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle action menu
  const handleActionClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedTransactionId(id);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedTransactionId(null);
  };

  // Action handlers
  const handleViewTransaction = (id: string) => {
    console.log(`View transaction ${id}`);
    handleActionClose();
    // TODO: Implement view transaction details
  };

  const handleEditTransaction = (id: string) => {
    console.log(`Edit transaction ${id}`);
    handleActionClose();
    // TODO: Implement edit transaction
  };

  // Handle add new income
  const handleAddIncome = () => {
    navigate('/add-income');
  };

  // Calculate total income from filtered transactions
  const totalIncome = filteredSales.reduce((sum: number, sale: Sale) => sum + sale.total, 0);

  return (
    <Box>
      {/* Header with title and actions */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>Income</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddIncome}
        >
          Add Income
        </Button>
      </Box>

      {/* Date range selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Typography variant="subtitle1" sx={{ minWidth: 100 }}>Date Range:</Typography>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <Typography variant="body1">to</Typography>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={() => fetchSales()}
            >
              Refresh
            </Button>
          </Stack>
        </LocalizationProvider>
      </Paper>

      {/* Income Summary Cards */}
      <Typography variant="h6" sx={{ mb: 2 }}>Financial Management</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 3 }}>
        {/* Total Sales Card */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">Total Sales (Period)</Typography>
          <Typography variant="h5" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
            {formatCurrency(totalIncome)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredSales.length} sales
          </Typography>
        </Paper>

        {/* Payment Method Card */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">By Payment Method</Typography>
          <Box sx={{ mt: 2 }}>
            {(() => {
              // Calculate totals by payment method
              const methodTotals = filteredSales.reduce((acc: Record<string, number>, sale: Sale) => {
                const method = sale.paymentMethod;
                acc[method] = (acc[method] || 0) + sale.total;
                return acc;
              }, {});
              
              // Display only payment methods with transactions
              return Object.entries(methodTotals)
                .sort(([, amountA], [, amountB]) => (amountB as number) - (amountA as number))
                .map(([method, amount]) => (
                  <Box key={method} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {paymentMethodDisplayNames[method] || method}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(amount as number)}
                    </Typography>
                  </Box>
                ));
            })()}
          </Box>
        </Paper>
      </Box>

      {/* By Category Card */}
      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">By Category</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mt: 2 }}>
            {/* Product Sales */}
            {(() => {
              // Calculate product sales total with proportional discount
              const productSales = filteredSales.reduce((total, sale) => {
                // Calculate sale subtotal
                const saleSubtotal = sale.items.reduce((sum, item) => sum + item.total, 0);

                // Calculate product subtotal for this sale
                const productSubtotal = sale.items
                  .filter(item => item.type === 'product')
                  .reduce((sum, item) => sum + item.total, 0);

                // Apply proportional discount if exists
                const saleDiscount = sale.discount || 0;
                let productTotal = productSubtotal;
                if (saleDiscount > 0 && saleSubtotal > 0) {
                  const productDiscountAmount = (productSubtotal / saleSubtotal) * saleDiscount;
                  productTotal = productSubtotal - productDiscountAmount;
                }

                return total + productTotal;
              }, 0);

              // Count product items sold
              const productItemsCount = filteredSales.reduce((count, sale) => {
                const productItems = sale.items.filter(item => item.type === 'product').length;
                return count + productItems;
              }, 0);

              return (
                <Box>
                  <Typography variant="subtitle2">Product Sales</Typography>
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                    {formatCurrency(productSales)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {productItemsCount} items sold
                  </Typography>
                </Box>
              );
            })()}

            {/* Service Sales */}
            {(() => {
              // Calculate service sales total with proportional discount
              const serviceSales = filteredSales.reduce((total, sale) => {
                // Calculate sale subtotal
                const saleSubtotal = sale.items.reduce((sum, item) => sum + item.total, 0);

                // Calculate service subtotal for this sale
                const serviceSubtotal = sale.items
                  .filter(item => item.type === 'service')
                  .reduce((sum, item) => sum + item.total, 0);

                // Apply proportional discount if exists
                const saleDiscount = sale.discount || 0;
                let serviceTotal = serviceSubtotal;
                if (saleDiscount > 0 && saleSubtotal > 0) {
                  const serviceDiscountAmount = (serviceSubtotal / saleSubtotal) * saleDiscount;
                  serviceTotal = serviceSubtotal - serviceDiscountAmount;
                }

                return total + serviceTotal;
              }, 0);

              // Count services provided
              const servicesCount = filteredSales.reduce((count, sale) => {
                const serviceItems = sale.items.filter(item => item.type === 'service').length;
                return count + serviceItems;
              }, 0);

              return (
                <Box>
                  <Typography variant="subtitle2">Service Sales</Typography>
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                    {formatCurrency(serviceSales)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {servicesCount} services provided
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        </Paper>
      </Box>

      {/* Income transactions list */}
      <Paper>
        {/* Search and filter toolbar */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            placeholder="Search income..."
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', sm: 250 } }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="method-filter-label">Payment Method</InputLabel>
              <Select
                labelId="method-filter-label"
                value={methodFilter}
                label="Payment Method"
                onChange={handleMethodChange}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method === 'All Methods' ? method : paymentMethodDisplayNames[method] || method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusChange}
              >
                {statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status === 'All Statuses' ? status : statusDisplayNames[status] || status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={handleFilterClick}>
              <FilterIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && !loading && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && !error && filteredSales.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No income transactions found.
            </Typography>
          </Box>
        )}

        {/* Transactions table */}
        {!loading && !error && filteredSales.length > 0 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Sale #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSales
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>
                          {sale.createdAt ? formatDate(sale.createdAt) : 'N/A'}
                        </TableCell>
                        <TableCell>{sale.saleNumber}</TableCell>
                        <TableCell>
                          {sale.customerDetails?.name || 'Walk-in Customer'}
                        </TableCell>
                        <TableCell>{sale.staffName}</TableCell>
                        <TableCell>
                          {getSaleCategories(sale).map((category) => (
                            <Chip 
                              key={category} 
                              label={category} 
                              size="small" 
                              sx={{ mr: 0.5, mb: 0.5 }} 
                            />
                          ))}
                        </TableCell>
                        <TableCell>
                          {paymentMethodDisplayNames[sale.paymentMethod] || sale.paymentMethod}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusDisplayNames[sale.paymentStatus] || sale.paymentStatus}
                            size="small"
                            color={
                              sale.paymentStatus === 'paid' ? 'success' :
                              sale.paymentStatus === 'partially_paid' ? 'warning' : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sale.total)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionClick(e, sale.id || '')}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredSales.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Filter menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem onClick={handleFilterClose}>
          Clear Filters
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleFilterClose}>
          Filter by Date
        </MenuItem>
        <MenuItem onClick={handleFilterClose}>
          Filter by Amount
        </MenuItem>
      </Menu>

      {/* Action menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => selectedTransactionId && handleViewTransaction(selectedTransactionId)}>
          <ListItemIcon>
            <ReceiptIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={() => selectedTransactionId && handleEditTransaction(selectedTransactionId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Income;
